import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    Container,
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Chip,
    Stack,
    Paper,
    Divider,
    IconButton,
    Tooltip,
    Avatar,
    LinearProgress,
    Fade,
    Slide,
    Zoom,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Badge,
    CircularProgress
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Visibility as VisibilityIcon,
    Analytics as AnalyticsIcon,
    TrendingUp as TrendingUpIcon,
    School as SchoolIcon,
    CalendarToday as CalendarIcon,
    OpenInNew as OpenInNewIcon,
    Download as DownloadIcon,
    Share as ShareIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Sort as SortIcon,
    BarChart as BarChartIcon,
    Person as PersonIcon,
    Article as ArticleIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import NavigationMenu from '../navigation/NavigationMenu';
import { headers } from '../../utilities/Env';
import { daysPassed, getIdCategory, getIdFullName } from '../../utilities/Functions';
import { APIResearcherList, APIUniversitiesList, APICategoriesList, APIResearchViewsDelete } from '../../utilities/APIS';
import useCustomToast from '../../hooks/useToast';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import LoadingState from '../../components/LoadingState';

const StyledCard = styled(Card)(({ theme }) => ({
    transition: 'all 0.3s ease',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.divider}`,
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: theme.shadows[16],
        borderColor: theme.palette.primary.main
    }
}));

const MetricBadge = styled(Box)(({ theme, color = 'primary' }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: '20px',
    backgroundColor: theme.palette[color].light,
    color: theme.palette[color].dark,
    fontSize: '0.75rem',
    fontWeight: '600'
}));

const ResearchViewAll = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showToast = useCustomToast();
    const [deletingProductId, setDeletingProductId] = useState(null);

    const [researchViews, setResearchViews] = useState([]);
    const [filteredViews, setFilteredViews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [sortOrder, setSortOrder] = useState('newest');
    const [researchersData, setResearchersData] = useState([]);
    const [universitiesData, setUniversitiesData] = useState([]);
    const [areasData, setAreasData] = useState([]);

    // Get data from location state or load from API
    const locationData = location.state?.researchViews || [];

    useEffect(() => {
        fetchAdditionalData();
        if (locationData.length > 0) {
            setResearchViews(locationData);
            setFilteredViews(locationData);
            setLoading(false);
        } else {
            loadResearchViews();
        }
    }, []);

    useEffect(() => {
        filterAndSortData();
    }, [researchViews, searchTerm, sortOrder, activeTab]);

    const fetchAdditionalData = async () => {
        try {
            const [researchersRes, uniRes, catsRes] = await Promise.all([
                axios.get(APIResearcherList, { headers }),
                axios.get(APIUniversitiesList, { headers }),
                axios.get(APICategoriesList, { headers })
            ]);

            setResearchersData(researchersRes.data.data || []);
            setUniversitiesData(uniRes.data.data || []);
            setAreasData(catsRes.data.data || []);
        } catch (error) {
            console.error('Error fetching additional data:', error);
        }
    };

    const loadResearchViews = async () => {
        setLoading(true);
        try {
            // You would replace this with your actual API call
            // const response = await axios.get(APIResearchViewsList, { headers });
            // setResearchViews(response.data.data || []);
            
            // For now, using mock data or empty array
            setResearchViews([]);
            setFilteredViews([]);
        } catch (error) {
            showToast("Error", "Failed to load research views", "error");
            console.error('Error loading research views:', error);
        } finally {
            setLoading(false);
        }
    };

    const filterAndSortData = () => {
        let filtered = researchViews;

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(view =>
                view.researchTopic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                getIdCategory(view.researchArea, areasData)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                view.researchStatus?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by tab
        if (activeTab === 1) {
            filtered = filtered.filter(view => view.researchStatus === 'Completed');
        } else if (activeTab === 2) {
            filtered = filtered.filter(view => view.researchStatus === 'Published');
        } else if (activeTab === 3) {
            filtered = filtered.filter(view => view.researchStatus === 'Work In Progress');
        }

        // Sort data
        filtered = [...filtered].sort((a, b) => {
            switch (sortOrder) {
                case 'newest':
                    return new Date(b.viewDate) - new Date(a.viewDate);
                case 'oldest':
                    return new Date(a.viewDate) - new Date(b.viewDate);
                case 'topic-asc':
                    return (a.researchTopic || '').localeCompare(b.researchTopic || '');
                case 'topic-desc':
                    return (b.researchTopic || '').localeCompare(a.researchTopic || '');
                default:
                    return 0;
            }
        });

        setFilteredViews(filtered);
    };

    const goToResearchDetails = (researchView) => {
        const detailsProps = {
            researchTopic: researchView.researchTopic,
            researcherName: researchView.researcherName,
            researchArea: researchView.researchArea,
            researchStatus: researchView.researchStatus,
            pdfFile: researchView.fileName,
            viewDate: researchView.viewDate,
            viewedBy: researchView.researchViewer
        };
        navigate("/research-details", { state: detailsProps });
    };

    const getViewerInfo = (viewerId) => {
        const viewer = researchersData.find(res => res.id.toString() === viewerId.toString());
        return viewer ? {
            name: viewer.full_name || viewer.name,
            title: viewer.title,
            university: viewer.university
        } : null;
    };

    const handleShare = async (researchView) => {
        try {
            await navigator.clipboard.writeText(window.location.origin + `/research-details`);
            showToast("Success", "Research link copied to clipboard", "success");
        } catch (error) {
            showToast("Error", "Failed to copy link", "error");
        }
    };

    const handleDownload = (researchView) => {
        // Implement download functionality
        showToast("Info", "Download functionality to be implemented", "info");
    };

    const renderResearchViewCard = (researchView, index) => {
        const viewerInfo = getViewerInfo(researchView.researchViewer);
        const daysSinceView = daysPassed(researchView.viewDate);

        return (
            <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                <StyledCard>
                    <CardContent sx={{ p: 3 }}>
                        <Stack spacing={2}>
                            {/* Header Section */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <AnalyticsIcon color="primary" />
                                        <Typography variant="h6" color="primary" fontWeight="bold">
                                            Research View Analytics
                                        </Typography>
                                    </Box>
                                    <Typography  fontWeight="bold" sx={{ mb: 1 }}>
                                        {researchView.researchTopic}
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    endIcon={<OpenInNewIcon />}
                                    onClick={() => goToResearchDetails(researchView)}
                                    size="small"
                                >
                                    View Research
                                </Button>
                            </Box>

                            {/* Viewer Information */}
                            {viewerInfo && (
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    color: 'black'
                                }}>
                                    <Avatar sx={{  color: 'primary.main' }}>
                                        <PersonIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Viewed by: {viewerInfo.name}
                                        </Typography>
                                        {/* <Typography  sx={{ opacity: 0.9 }}>
                                            {viewerInfo.title} • thissss {getIdCategory(viewerInfo.university, universitiesData)}
                                        </Typography> */}
                                    </Box>
                                </Box>
                            )}

                            {/* Metrics Grid */}
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <MetricBadge color="primary">
                                        <CalendarIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                        Viewed {daysSinceView} days ago
                                    </MetricBadge>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <MetricBadge color="secondary">
                                        <ArticleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                        {researchView.researchStatus}
                                    </MetricBadge>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <MetricBadge color="success">
                                        <SchoolIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                        {getIdCategory(researchView.researchArea, areasData)}
                                    </MetricBadge>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <MetricBadge color="info">
                                        <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                        Uploaded: {researchView.uploadDate}
                                    </MetricBadge>
                                </Grid>
                            </Grid>

                            <Divider />

                            {/* Action Buttons */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Tooltip title="View Research Details">
                                        <IconButton 
                                            color="primary"
                                            onClick={() => goToResearchDetails(researchView)}
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download Research">
                                        <IconButton 
                                            color="secondary"
                                            onClick={() => handleDownload(researchView)}
                                        >
                                            <DownloadIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Share Research">
                                        <IconButton 
                                            color="info"
                                            onClick={() => handleShare(researchView)}
                                        >
                                            <ShareIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Research View">
                                        <IconButton 
                                            color="error"
                                            onClick={() => handleDelete (researchView)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    View recorded on: {researchView.viewDate}
                                </Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </StyledCard>
            </Zoom>
        );
    };

    const renderEmptyState = () => (
        <Fade in={true}>
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                <AnalyticsIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                    No Research Views Yet
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Your research hasn't been viewed by other researchers yet. 
                    Share your work to increase visibility and engagement.
                </Typography>
                <Button 
                    variant="contained" 
                    size="large"
                    onClick={() => navigate('/researches')}
                    startIcon={<ShareIcon />}
                >
                    Share Your Research
                </Button>
            </Paper>
        </Fade>
    );

    const renderStats = () => (
        <Slide direction="down" in={true}>
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3, background: theme.palette.success.main, color: 'white' }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            Research Views Analytics
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.9 }}>
                            Track how researchers are engaging with your work
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Box textAlign="center">
                                    <Typography variant="h3" fontWeight="bold">
                                        {researchViews.length}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Total Views
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box textAlign="center">
                                    <Typography variant="h3" fontWeight="bold">
                                        {new Set(researchViews.map(v => v.researchViewer)).size}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Unique Viewers
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box textAlign="center">
                                    <Typography variant="h3" fontWeight="bold">
                                        {new Set(researchViews.map(v => v.researchTopic)).size}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Research Works
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>
        </Slide>
    );

      const handleDelete = async (product) => {
                // Show custom confirmation dialog
                const confirmDelete = window.confirm(
                    `🚨 Delete Confirmation\n\n` +
                    `View: ${product.researchTopic}\n` +
                    `Researcher: ${getIdFullName(product.researcherName, researchersData)}\n` +
                    `This action cannot be undone. Are you sure you want to delete this research view?`
                );
        
                if (!confirmDelete) {
                    showToast("Cancelled", "Record deletion was cancelled", "info");
                    return;
                }
        
                // Set loading state for this specific product
                setDeletingProductId(product.id);
        
                try {
                    // Create form data with product ID
                    const formDataToSend = new FormData();
                    formDataToSend.append('updateId', product.id);
        
                    // Make DELETE request
                    const response = await axios.post(APIResearchViewsDelete, formDataToSend, {headers});
        
                    if (response.data.status === "success") {
                        const successMessage = response.data.message || "Record deleted successfully!";
                        showToast("Success", successMessage, "success");
                        // Update local state by removing the deleted product
                        setResearchViews(prev => prev.filter(p => p.id !== product.id));
                        setFilteredViews(prev => prev.filter(p => p.id !== product.id));
        
                    } else {
                        throw new Error(response.data.message || "Unknown server error");
                    }
                } catch (error) {
                    // console.error('Delete product error:', error);
                    let errorTitle = "Delete Failed";
                    let errorMessage = "Failed to delete record. Please try again.";
                    if (error.response) {
                        const status = error.response.status;
                        switch (status) {
                            case 422:
                                const errors = error.response.data.errors;
                                errorMessage = Object.values(errors).flat().join(', ') || "Validation error";
                                break;
                            case 404:
                                errorMessage = "Record not found. It may have been already deleted.";
                                break;
                            case 403:
                                errorMessage = "You don't have permission to delete this product.";
                                break;
                            case 401:
                                errorMessage = "Please log in to perform this action.";
                                break;
                            case 500:
                                errorMessage = "Server error. Please try again later.";
                                break;
                            default:
                                errorMessage = error.response.data?.message || `Server error (${status})`;
                        }
                    } else if (error.code === "ERR_NETWORK") {
                        errorMessage = TEXT_CONNECTION_ERROR;
                    } else if (error.code === "ECONNABORTED") {
                        errorMessage = "Request timeout. Please check your connection and try again.";
                    } else if (error.message) {
                        errorMessage = error.message;
                    }
        
                    showToast(errorTitle, errorMessage, "error");
                } finally {
                    // Clear loading state
                    setDeletingProductId(null);
                }
            };
if (loading) {
    return (
        <>
            <NavigationMenu />
            <LoadingState/>
        </>
    );
}
    return (
        <>
            <NavigationMenu />
            <Container maxWidth="lg" sx={{ mt: 8, py: 4 }}>
                {/* Header */}
                {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                    <Tooltip title="Go Back">
                        <IconButton 
                            onClick={() => navigate(-1)}
                            sx={{ mr: 2 }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                    <Box>
                        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                            Research Views Analytics
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            Monitor engagement with your research publications
                        </Typography>
                    </Box>
                </Box> */}

                {/* Stats Section */}
                {researchViews.length > 0 && renderStats()}

                {/* Filters and Search */}
                {researchViews.length > 0 && (
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    placeholder="Search research topics, areas..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="primary" />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                                        <Tab label="All Views" />
                                        <Tab label="Completed" />
                                        <Tab label="Published" />
                                        <Tab label="In Progress" />
                                    </Tabs>
                                </Box>
                            </Grid>
                        </Grid>
                        <br></br>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" color="secondary">
                                Showing {filteredViews.length} of {researchViews.length} research views
                            </Typography>
                        </Box>
                    </Paper>
                )}

                {/* Content */}
                {researchViews.length === 0 ? (
                    renderEmptyState()
                ) : (
                    <Stack spacing={3}>
                        {filteredViews.map((view, index) => renderResearchViewCard(view, index))}
                    </Stack>
                )}
            </Container>
        </>
    );
};

export default ResearchViewAll;